import { useEffect } from 'react';

// Per-recipe <title>, description and Recipe structured data (#740).
//
// This lives in the React route on purpose. Crawlers now receive the REAL
// page -- corebyscott renders it through Chromium and stores the result -- so
// whatever this page puts in its head is what search engines read. The
// alternative was a second copy of each recipe's fields in Ruby, which would
// drift from this component the first time a field was added to one and not
// the other.
//
// config/seo.yml cannot do this job: it is keyed by path, so "/recipes/:id"
// can only ever carry one title for all 236 recipes.
//
// Structured data is the cheapest win available here. A Recipe block is what
// makes Google show ingredients and times in the result itself rather than a
// plain link.

const TAG_MARK = 'data-recipe-seo';

function upsertMeta(attr, value, content) {
  let el = document.head.querySelector(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    el.setAttribute(TAG_MARK, 'true');
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

// Minutes -> an ISO-8601 duration, the only form schema.org accepts.
function isoDuration(minutes) {
  const n = Number(minutes);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `PT${Math.round(n)}M`;
}

function buildSchema(recipe) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.title,
  };
  if (recipe.description) schema.description = recipe.description;
  if (recipe.servings > 0) schema.recipeYield = String(recipe.servings);

  const prep = isoDuration(recipe.prep_time_minutes);
  const cook = isoDuration(recipe.cook_time_minutes);
  const total = isoDuration(recipe.total_time_minutes);
  if (prep) schema.prepTime = prep;
  if (cook) schema.cookTime = cook;
  if (total) schema.totalTime = total;

  const ingredients = (recipe.ingredients || [])
    .map((i) => i.display_text || i.name)
    .filter(Boolean);
  if (ingredients.length) schema.recipeIngredient = ingredients;

  // Split on blank lines so multi-step instructions become steps rather than
  // one wall of text, which is what a step-by-step result needs.
  const steps = (recipe.instructions || '')
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (steps.length) {
    schema.recipeInstructions = steps.map((text) => ({ '@type': 'HowToStep', text }));
  }

  const categories = (recipe.categories || []).map((c) => c.name).filter(Boolean);
  if (categories.length) schema.recipeCategory = categories;

  return schema;
}

function clear(defaultTitle) {
  document.title = defaultTitle;
  document.head.querySelectorAll(`[${TAG_MARK}]`).forEach((el) => el.remove());
}

export default function useRecipeSeo(recipe, { defaultTitle = 'RecipesByScott' } = {}) {
  useEffect(() => {
    if (!recipe || !recipe.title) {
      clear(defaultTitle);
      return undefined;
    }

    document.title = `${recipe.title} — RecipesByScott`;
    if (recipe.description) {
      upsertMeta('name', 'description', recipe.description);
      upsertMeta('property', 'og:description', recipe.description);
    }
    upsertMeta('property', 'og:title', recipe.title);
    upsertMeta('property', 'og:type', 'article');

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(TAG_MARK, 'true');
    // textContent, never innerHTML: the browser does not parse this as markup,
    // so a recipe title containing a closing script tag cannot break out.
    script.textContent = JSON.stringify(buildSchema(recipe));
    document.head.appendChild(script);

    // Removed on unmount so navigating between recipes cannot leave two
    // ld+json blocks, which would offer a crawler two different recipes for
    // one url.
    return () => clear(defaultTitle);
  }, [recipe, defaultTitle]);
}
